import type { Extend } from '@mentimeter/ragnar-dsc';
import { addUnit } from '@mentimeter/ragnar-utils';

const getQuoteIcon = (color: string) => `
<svg width="36" height="36" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
<g opacity="0.1">
<path d="M10.0948 30.375C8.2348 30.375 6.68477 29.6186 5.44475 28.1057C4.25242 26.5928 3.65625 24.5125 3.65625 21.865C3.65625 18.2718 4.56242 15.0806 6.37477 12.2912C8.2348 9.50179 10.7625 7.27973 13.958 5.625L15.8896 9.02901C13.5049 10.4001 11.6687 12.1257 10.381 14.2059C9.09328 16.2389 8.44942 18.6028 8.44942 21.2976L9.80868 26.8292L5.87399 22.1486C6.494 21.4867 7.18555 20.9431 7.94864 20.5175C8.75943 20.0448 9.57021 19.8084 10.381 19.8084C11.8595 19.8084 13.0518 20.2812 13.958 21.2267C14.9119 22.1723 15.3888 23.4015 15.3888 24.9144C15.3888 26.5218 14.888 27.8456 13.8865 28.8857C12.9326 29.8786 11.6687 30.375 10.0948 30.375ZM26.549 30.375C24.689 30.375 23.1389 29.6186 21.8989 28.1057C20.7066 26.5928 20.1104 24.5125 20.1104 21.865C20.1104 18.2718 21.0166 15.0806 22.8289 12.2912C24.689 9.50179 27.2167 7.27973 30.4122 5.625L32.3438 9.02901C29.9591 10.4001 28.1229 12.1257 26.8352 14.2059C25.5475 16.2389 24.9036 18.6028 24.9036 21.2976L26.2629 26.8292L22.3282 22.1486C22.9482 21.4867 23.6397 20.9431 24.4028 20.5175C25.2136 20.0448 26.0244 19.8084 26.8352 19.8084C28.3137 19.8084 29.506 20.2812 30.4122 21.2267C31.366 22.1723 31.843 23.4015 31.843 24.9144C31.843 26.5218 31.3422 27.8456 30.3406 28.8857C29.3868 29.8786 28.1229 30.375 26.549 30.375Z" fill="${color}"/>
</g>
</svg>

`;

export const canvasBlockStylesExtend: Extend = ({ theme }) => {
  const svg = encodeURIComponent(getQuoteIcon(theme.colors.text));
  return {
    lineHeight: 1.3,
    '& p': {
      whiteSpaceCollapse: 'collapse',
      '&:not(:last-child)': {
        marginBottom: `${theme.kosmosSpacing.space6}px`,
      },
    },
    // This should be renamed to h1 when below h1 is removed (https://linear.app/mentimeter/issue/CX-1908/clean-up-old-display-heading-css)
    '& h1.canvas-display-new': {
      fontFamily: theme.fonts.heading,
      fontSize: '14rem',
      fontWeight: theme.fontWeights.regular,
      lineHeight: theme.kosmosLineHeight.relative.none,
      whiteSpaceCollapse: 'collapse',
      '&:not(:last-child)': {
        marginBottom: '36px',
      },
    },
    // Legacy. Should be cleaned with above task.
    '& h1': {
      fontFamily: theme.fonts.heading,
      fontSize: '5.375rem',
      fontWeight: theme.fontWeights.regular,
      lineHeight: theme.kosmosLineHeight.relative.tight,
      '&:not(:last-child)': {
        marginBottom: `${theme.kosmosSpacing.space6}px`,
      },
    },
    '& h2': {
      fontFamily: theme.fonts.heading,
      fontSize: '5.375rem',
      fontWeight: theme.fontWeights.regular,
      lineHeight: theme.kosmosLineHeight.relative.tight,
      whiteSpaceCollapse: 'collapse',
      '&:not(:last-child)': {
        marginBottom: `${theme.kosmosSpacing.space6}px`,
      },
    },
    '& h3': {
      fontFamily: theme.fonts.heading,
      fontSize: addUnit(theme.kosmosFontSize[300]),
      fontWeight: theme.fontWeights.regular,
      lineHeight: theme.kosmosLineHeight.relative.tight,
      whiteSpaceCollapse: 'collapse',
      '&:not(:last-child)': {
        marginBottom: `${theme.kosmosSpacing.space6}px`,
      },
    },
    '& small': {
      fontSize: addUnit(theme.kosmosFontSize[150]),
      display: 'block',
      whiteSpaceCollapse: 'collapse',
      '&:not(:last-child)': {
        marginBottom: `${theme.kosmosSpacing.space3}px`,
      },
    },
    '& ol': {
      display: 'inline-block',
      width: 'auto',
      textAlign: 'start',
      marginLeft: '30px',
      '& ol': {
        listStyleType: 'lower-alpha',
        '& ol': {
          listStyleType: 'lower-roman',
        },
      },
      '&:not(:last-child)': {
        marginBottom: `${theme.kosmosSpacing.space6}px`,
      },
      '& li:not(:last-child)': {
        marginBottom: `${theme.kosmosSpacing.space3}px`,
      },
      '& li p:not(:last-child)': {
        marginBottom: `${theme.kosmosSpacing.space3}px`,
      },
    },
    '& ul': {
      display: 'inline-block',
      width: 'auto',
      textAlign: 'start',
      marginLeft: '30px',
      '&:not(:last-child)': {
        marginBottom: `${theme.kosmosSpacing.space6}px`,
      },
      '& li:not(:last-child)': {
        marginBottom: `${theme.kosmosSpacing.space3}px`,
      },
      '& li p:not(:last-child)': {
        marginBottom: `${theme.kosmosSpacing.space3}px`,
      },
    },
    // inline code
    '& code': {
      padding: `${theme.kosmosSpacing.space1}px ${theme.kosmosSpacing.space2}px`,
      borderRadius: `${theme.kosmosBorderRadius['xl']}px`,
      background: theme.colors.neutralWeak,
    },
    // code block
    '& pre': {
      '&:not(:last-child)': {
        marginBottom: `${theme.kosmosSpacing.space6}px`,
      },
      '& code': {
        display: 'inline-block',
        width: 'auto',
        textAlign: 'start',
        padding: `${theme.kosmosSpacing.space6}px`,
        borderRadius: `${theme.kosmosBorderRadius['4xl']}px`,
        background: theme.colors.neutralWeak,
      },
    },
    '& blockquote': {
      fontSize: '5.375rem',
      width: '100%',
      padding: `${theme.kosmosSpacing.space6}px`,
      borderRadius: `${theme.kosmosBorderRadius['xl']}px`,
      background: theme.colors.neutralWeak,
      fontWeight: theme.fontWeights.regular,
      whiteSpaceCollapse: 'collapse',
      '&:not(:last-child)': {
        marginBottom: `${theme.kosmosSpacing.space6}px`,
      },
      /** Placeholder is using ::before to set the 'Write text here' so we need to override it with !important on content and height here */
      '&::before': {
        content: '"" !important',
        backgroundImage: `url('data:image/svg+xml,${svg}')`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        height: '36px !important',
        width: '36px',
        display: 'inline-block',
      },
      '&::after': {
        rotate: '180deg',
        content: '""',
        backgroundImage: `url('data:image/svg+xml,${svg}')`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        height: '36px',
        width: '36px',
        display: 'inline-block',
      },
    },
  };
};
